<script setup>
import { useTranslations } from '@/translation'
import { useWidget } from '@/widget'
import { ref, computed } from 'vue'
import Emitter from '@/emit.js'
import ModalContextMenu from '@/components/modals/ModalContextMenu.vue'
import ModalContentText from '@/components/modals/ModalContentText.vue'
import { RefreshCcw, EllipsisVertical, X } from 'lucide-vue-next'
import { Minimize2 } from 'lucide-vue-next'
import { Maximize2 } from 'lucide-vue-next'

const { t } = useTranslations()

const { config, options, sendPostMessage, getOption } = useWidget()
const clearSession = () => {
  Emitter.emit('reset')
}
const hasMenu = computed(() => getOption('menu').length > 0)
const onShowRodo = (menu) => {
  hiddenContextMenu()
  textToModalConent.value = menu.text
  titleToModalConent.value = t('context.button.rodo')
  setTimeout(() => {
    showContentText.value = true
  }, 300)
}
const showContentText = ref(false)
const contextMenuOpen = ref(false)
const titleToModalConent = ref('')
const textToModalConent = ref('')

const showContextMenu = () => {
  contextMenuOpen.value = true
}
const hiddenContextMenu = () => {
  contextMenuOpen.value = false
}
const extend_chat = ref(false)
const resize = () => {
  extend_chat.value = !extend_chat.value
  Emitter.emit('resize', extend_chat.value)
  sendPostMessage({ resize_chat: extend_chat.value }, 'resize_chat')
}
const close = () => {
  sendPostMessage({ close_chat: true }, 'close_chat')
  Emitter.emit('close', extend_chat.value)
}
const hasHistory = computed(() => {
  return getOption('chat_id') !== ''
})
</script>

<template>
  <div
    class="flex flex-col justify-center px-4 shadow-xl h-20 relative"
    :class="{ 'border-b border-b-appBorder': config.specialStyles }"
  >
    <div class="flex items-center gap-4">
      <img
        v-if="options.assistantImage"
        :src="options.assistantImage"
        alt="Assistant"
        class="w-10 h-10 rounded-full object-cover"
      />
      <div class="flex flex-col">
        <h2 class="font-semibold text-lg tracking-tight">{{ options.assistantName }}</h2>
        <p class="text-xs text-primary">{{ t('assistant.status') }}</p>
      </div>
    </div>

    <div class="absolute top-1/2 -translate-y-1/2 right-4 flex items-center gap-2 opacity-50">
      <button
        v-tooltip="t('buttons.newChat')"
        @click="clearSession"
        v-if="!config.fullScreen && hasHistory"
        class="rounded-full hover:bg-gray/10 focus:bg-gray/10 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-colors duration-200"
        type="button"
        :aria-label="t('buttons.newChat')"
      >
        <RefreshCcw width="18" aria-hidden="true" />
      </button>
      <button
        @click="resize"
        v-if="!config.fullScreen"
        class="rounded-full hover:bg-gray/10 focus:bg-gray/10 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-colors duration-200"
        type="button"
        :aria-label="extend_chat ? t('buttons.minimizeChat') : t('buttons.maximizeChat')"
      >
        <template v-if="extend_chat">
          <Minimize2 aria-hidden="true" />
        </template>
        <template v-else>
          <Maximize2 aria-hidden="true" />
        </template>
      </button>
      <button
        v-if="hasMenu"
        @click="showContextMenu"
        class="flex items-center gap-2 rounded-full hover:bg-gray/10 focus:bg-gray/10 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-colors duration-200"
        type="button"
        :aria-label="t('aria.menu')"
        aria-haspopup="dialog"
        :aria-expanded="contextMenuOpen"
      >
        <EllipsisVertical aria-hidden="true" />
        <span class="sr-only">{{ t('aria.menu') }}</span>
      </button>
      <button
        v-if="!config.fullScreen"
        @click="close"
        class="rounded-full hover:bg-gray/10 focus:bg-gray/10 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-colors duration-200"
        type="button"
        :aria-label="t('buttons.closeChat')"
      >
        <X aria-hidden="true" />
      </button>
    </div>
    <ModalContextMenu
      v-model="contextMenuOpen"
      @update:model-value="contextMenuOpen = false"
      @show:rodo="onShowRodo"
    />
    <ModalContentText
      v-model="showContentText"
      :title="titleToModalConent"
      :text="textToModalConent"
    />
  </div>
</template>
